import { z } from "zod/v3";

export const SlaStateSchema = z.enum([
  "ok",
  "needs_attention",
  "breached",
  "paused",
  "closed",
]);
export type SlaState = z.infer<typeof SlaStateSchema>;

export const SlaTriggerTypeSchema = z.enum([
  "finding_first_detected",
  "ces_due_date",
]);
export type SlaTriggerType = z.infer<typeof SlaTriggerTypeSchema>;

export const SlaRuleSchema = z.object({
  id: z.string().optional(),
  days: z.number().int().positive(),
  warningDays: z.number().int().nonnegative().optional(),
  severity: z.enum(["critical", "high", "medium", "low"]).optional(),
  sourceType: z.string().optional(),
  itemType: z.string().optional(),
});
export type SlaRuleDTO = z.infer<typeof SlaRuleSchema>;

export const SlaPolicyDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  enabled: z.boolean(),
  criticalDays: z.number().int().positive(),
  highDays: z.number().int().positive(),
  mediumDays: z.number().int().positive(),
  lowDays: z.number().int().positive(),
  warningDays: z.number().int().nonnegative(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type SlaPolicyDTO = z.infer<typeof SlaPolicyDTOSchema>;

export const SlaPolicyInputSchema = z.object({
  enabled: z.boolean().optional(),
  criticalDays: z.number().int().positive(),
  highDays: z.number().int().positive(),
  mediumDays: z.number().int().positive(),
  lowDays: z.number().int().positive(),
  warningDays: z.number().int().nonnegative(),
});
export type SlaPolicyInputDTO = z.infer<typeof SlaPolicyInputSchema>;

export const SlaItemSnapshotDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  policyId: z.string(),
  controlEvidenceStatusId: z.string().nullable(),
  sourceType: z.string(),
  sourceId: z.string(),
  triggerType: SlaTriggerTypeSchema,
  severity: z.string().nullable(),
  triggeredAt: z.date(),
  deadlineAt: z.date(),
  ruleSnapshot: SlaRuleSchema.nullable(),
  policySnapshot: z.record(z.unknown()),
  stateAtSnapshot: SlaStateSchema,
  rebaselinedAt: z.date().nullable(),
  rebaselinedBy: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type SlaItemSnapshotDTO = z.infer<typeof SlaItemSnapshotDTOSchema>;

export const SlaEventDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  policyId: z.string().nullable(),
  snapshotId: z.string().nullable(),
  sourceType: z.string(),
  sourceId: z.string(),
  eventType: z.enum([
    "warning",
    "deadline",
    "breached",
    "resolved",
    "rebaselined",
    "cycle_reopened",
  ]),
  eventAt: z.date(),
  dedupeKey: z.string(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
});
export type SlaEventDTO = z.infer<typeof SlaEventDTOSchema>;

export const SlaAggregateDTOSchema = z.object({
  state: SlaStateSchema.nullable(),
  breachedCount: z.number().int().nonnegative(),
  needsAttentionCount: z.number().int().nonnegative(),
  okCount: z.number().int().nonnegative(),
  totalOpenItems: z.number().int().nonnegative(),
  nextDeadlineAt: z.date().nullable(),
});
export type SlaAggregateDTO = z.infer<typeof SlaAggregateDTOSchema>;

export const SlaEvaluatedItemDTOSchema = z.object({
  sourceType: z.string(),
  sourceId: z.string(),
  title: z.string(),
  severity: z.string().nullable(),
  triggeredAt: z.date().nullable(),
  deadlineAt: z.date().nullable(),
  slaState: SlaStateSchema,
  daysRemaining: z.number().int().nullable(),
  daysOverdue: z.number().int().nullable(),
  rule: SlaRuleSchema.nullable(),
  snapshotId: z.string().nullable().optional(),
  snapshotCreatedAt: z.date().nullable().optional(),
  rebaselinedAt: z.date().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type SlaEvaluatedItemDTO = z.infer<typeof SlaEvaluatedItemDTOSchema>;
